import { useState } from 'react';
import { Settings, User, AlertTriangle, FileText, Plus, X, Upload, Image as ImageIcon } from 'lucide-react';
import { useI18n } from '@/contexts/i18n-hook';
import { comprimirImagemParaDataUrl } from '@/utils/comprimir-imagem';

const formatExtraFieldLabel = (key: string): string =>
  key
    .replace(/\./g, ' / ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

// Componente para exibir imagem com tratamento de erro
function ImagemComFallback({ 
  src, 
  alt, 
  className 
}: { 
  src: string | undefined; 
  alt: string; 
  className?: string;
}) {
  const { t } = useI18n();
  const [erroCarregamento, setErroCarregamento] = useState(false);
  const [carregando, setCarregando] = useState(true);

  if (!src) {
    return (
      <div className={`flex items-center justify-center ${className || ''}`}>
        <p className="text-sm text-gray-500 dark:text-gray-400">{t('comum.arquivo.nenhumEscolhido')}</p>
      </div>
    );
  }

  if (erroCarregamento) {
    return (
      <div className={`flex flex-col items-center justify-center ${className || ''}`}>
        <AlertTriangle className="h-8 w-8 text-yellow-500 mb-2" />
        <p className="text-sm text-gray-500 dark:text-gray-400">{t('apreciacaoEdicao.informacoes.imagem.erroCarregar')}</p>
      </div>
    );
  }

  return (
    <div className="relative">
      {carregando && (
        <div className={`absolute inset-0 flex items-center justify-center bg-gray-100 dark:bg-schmersal-gray-dark ${className || ''}`}>
          <p className="text-sm text-gray-500 dark:text-gray-400">{t('comum.estado.carregando')}</p>
        </div>
      )}
      <img
        src={src}
        alt={alt}
        className={`${className || ''} ${carregando ? 'opacity-0' : 'opacity-100'} transition-opacity`}
        onLoad={() => setCarregando(false)}
        onError={() => {
          setErroCarregamento(true);
          setCarregando(false);
        }}
      />
    </div>
  );
}

interface InformacoesMaquinaProps {
  dados: {
    maquina?: {
      nome?: string;
      modelo?: string;
      fabricante?: string;
      site?: string;
      area?: string;
      local?: string;
    };
    operadorEntrevistado?: string;
    cargoOperador?: string;
    experiencia?: number;
    turnosTrabalho?: string;
    frequenciaUso?: string;
    modoOperacao?: string;
    historicoAcidentes?: string;
    limitesUso?: string;
    limitesEspaco?: string;
    limitesAdicionais?: string;
    limitesAmbientais?: string;
    limitacoes?: string;
    mauUsoRazoavelmentePrevisivel?: string[];
    normasTecnicasReferencia?: string[];
    nota1?: string;
    nota2?: string;
    observacoesEntrevista?: string;
    dataEntrevista?: string;
    frequenciaAcessoOperacao?: string;
    frequenciaAcessoManutencao?: string;
    comprimentoHorizontal?: string;
    elevacao?: string;
    capacidadeCorreia?: string;
    larguraCorreia?: string;
    velocidadeCorreia?: string;
    material?: string;
    contraRecuo?: string;
    freio?: string;
    potencia?: string;
    tensaoAcionamento?: string;
    tensaoComando?: string;
    espacoPecasFerramentas?: boolean;
    espacoOperacaoNormal?: boolean;
    espacoManutencao?: boolean;
    espacoAjuste?: boolean;
    espacoLimpeza?: boolean;
    fotoPanoramicaUrl?: string;
    fotoIdentificacaoUrl?: string;
    funcaoMaquina?: string;
  };
  extraFields?: Record<string, string>;
  onChange?: (campo: string, valor: any) => void;
  modoEdicao?: boolean;
}

export function InformacoesMaquina({
  dados,
  extraFields,
  onChange,
  modoEdicao = false,
}: InformacoesMaquinaProps) {
  const { t, formatarData } = useI18n();
  const [novoMauUso, setNovoMauUso] = useState('');
  const [novaNorma, setNovaNorma] = useState('');

  const adicionarMauUso = () => {
    if (novoMauUso.trim() && dados.mauUsoRazoavelmentePrevisivel) {
      const atual = dados.mauUsoRazoavelmentePrevisivel || [];
      if (atual.length < 5) {
        onChange?.('mauUsoRazoavelmentePrevisivel', [...atual, novoMauUso.trim()]);
        setNovoMauUso('');
      }
    }
  };

  const removerMauUso = (index: number) => {
    const atual = dados.mauUsoRazoavelmentePrevisivel || [];
    onChange?.('mauUsoRazoavelmentePrevisivel', atual.filter((_, i) => i !== index));
  };

  const adicionarNorma = () => {
    if (novaNorma.trim() && dados.normasTecnicasReferencia) {
      const atual = dados.normasTecnicasReferencia || [];
      onChange?.('normasTecnicasReferencia', [...atual, novaNorma.trim()]);
      setNovaNorma('');
    }
  };

  const removerNorma = (index: number) => {
    const atual = dados.normasTecnicasReferencia || [];
    onChange?.('normasTecnicasReferencia', atual.filter((_, i) => i !== index));
  };

  return (
    <div className="bg-white dark:bg-schmersal-gray-darker shadow rounded-lg p-6 space-y-6 border border-rg-line dark:border-schmersal-gray-dark">
      <div className="flex items-center mb-4">
        <Settings className="h-6 w-6 text-blue-500 dark:text-schmersal-blue mr-2" />
        <h2 className="text-xl font-bold text-gray-900 dark:text-white">
          {t('apreciacaoEdicao.informacoes.titulo')}
        </h2>
      </div>

      {/* Identificação da Máquina */}
      <div className="border-b dark:border-schmersal-gray-dark pb-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          {t('apreciacaoEdicao.informacoes.identificacao.titulo')}
        </h3>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-white mb-2">
              {t('apreciacaoEdicao.informacoes.identificacao.site')}
            </label>
            {modoEdicao ? (
              <input
                type="text"
                value={dados.maquina?.site || ''}
                onChange={(e) =>
                  onChange?.('maquina', {
                    ...(dados.maquina || {}),
                    site: e.target.value,
                  })
                }
                className="w-full border-gray-300 dark:border-schmersal-gray-dark rounded-md shadow-sm 
                         focus:ring-blue-500 dark:focus:ring-schmersal-blue focus:border-blue-500 dark:focus:border-schmersal-blue 
                         bg-white dark:bg-schmersal-gray-darkest text-gray-900 dark:text-white
                         sm:text-sm"
              />
            ) : (
              <p className="text-sm text-gray-900 dark:text-white">
                {dados.maquina?.site || '-'}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-white mb-2">
              {t('apreciacaoEdicao.informacoes.identificacao.areaSetor')}
            </label>
            {modoEdicao ? (
              <input
                type="text"
                value={dados.maquina?.area || ''}
                onChange={(e) =>
                  onChange?.('maquina', {
                    ...(dados.maquina || {}),
                    area: e.target.value,
                  })
                }
                className="w-full border-gray-300 dark:border-schmersal-gray-dark rounded-md shadow-sm 
                         focus:ring-blue-500 dark:focus:ring-schmersal-blue focus:border-blue-500 dark:focus:border-schmersal-blue 
                         bg-white dark:bg-schmersal-gray-darkest text-gray-900 dark:text-white
                         sm:text-sm"
              />
            ) : (
              <p className="text-sm text-gray-900 dark:text-white">
                {dados.maquina?.area || '-'}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-white mb-2">
              {t('comum.rotulo.local')}
            </label>
            {modoEdicao ? (
              <input
                type="text"
                value={dados.maquina?.local || ''}
                onChange={(e) =>
                  onChange?.('maquina', {
                    ...(dados.maquina || {}),
                    local: e.target.value,
                  })
                }
                className="w-full border-gray-300 dark:border-schmersal-gray-dark rounded-md shadow-sm 
                         focus:ring-blue-500 dark:focus:ring-schmersal-blue focus:border-blue-500 dark:focus:border-schmersal-blue 
                         bg-white dark:bg-schmersal-gray-darkest text-gray-900 dark:text-white
                         sm:text-sm"
              />
            ) : (
              <p className="text-sm text-gray-900 dark:text-white">
                {dados.maquina?.local || '-'}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-white mb-2">
              {t('apreciacaoEdicao.informacoes.identificacao.fabricante')}
            </label>
            {modoEdicao ? (
              <input
                type="text"
                value={dados.maquina?.fabricante || ''}
                onChange={(e) =>
                  onChange?.('maquina', {
                    ...(dados.maquina || {}),
                    fabricante: e.target.value,
                  })
                }
                className="w-full border-gray-300 dark:border-schmersal-gray-dark rounded-md shadow-sm 
                         focus:ring-blue-500 dark:focus:ring-schmersal-blue focus:border-blue-500 dark:focus:border-schmersal-blue 
                         bg-white dark:bg-schmersal-gray-darkest text-gray-900 dark:text-white
                         sm:text-sm"
              />
            ) : (
              <p className="text-sm text-gray-900 dark:text-white">
                {dados.maquina?.fabricante || '-'}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-white mb-2">
              {t('apreciacaoEdicao.informacoes.identificacao.modelo')}
            </label>
            {modoEdicao ? (
              <input
                type="text"
                value={dados.maquina?.modelo || ''}
                onChange={(e) =>
                  onChange?.('maquina', {
                    ...(dados.maquina || {}),
                    modelo: e.target.value,
                  })
                }
                className="w-full border-gray-300 dark:border-schmersal-gray-dark rounded-md shadow-sm 
                         focus:ring-blue-500 dark:focus:ring-schmersal-blue focus:border-blue-500 dark:focus:border-schmersal-blue 
                         bg-white dark:bg-schmersal-gray-darkest text-gray-900 dark:text-white
                         sm:text-sm"
              />
            ) : (
              <p className="text-sm text-gray-900 dark:text-white">
                {dados.maquina?.modelo || '-'}
              </p>
            )}
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-white mb-2">
              {t('apreciacaoEdicao.informacoes.identificacao.funcaoMaquina')}
            </label>
            {modoEdicao ? (
              <textarea
                value={dados.funcaoMaquina || ''}
                onChange={(e) => onChange?.('funcaoMaquina', e.target.value)}
                rows={4}
                className="w-full border-gray-300 dark:border-schmersal-gray-dark rounded-md shadow-sm 
                         focus:ring-blue-500 dark:focus:ring-schmersal-blue focus:border-blue-500 dark:focus:border-schmersal-blue 
                         bg-white dark:bg-schmersal-gray-darkest text-gray-900 dark:text-white
                         sm:text-sm"
                placeholder={t('apreciacaoEdicao.informacoes.identificacao.placeholderFuncao')}
              />
            ) : (
              <p className="text-sm text-gray-900 dark:text-white whitespace-pre-wrap">
                {dados.funcaoMaquina || '-'}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Entrevistados e Dados de Uso */}
      <div className="border-b dark:border-schmersal-gray-dark pb-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
          <User className="h-5 w-5 mr-2" />
          {t('apreciacaoEdicao.informacoes.entrevista.titulo')}
        </h3>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-white mb-2">
              {t('apreciacaoEdicao.informacoes.entrevista.operador')}
            </label>
            {modoEdicao ? (
              <input
                type="text"
                value={dados.operadorEntrevistado || ''}
                onChange={(e) =>
                  onChange?.('operadorEntrevistado', e.target.value)
                }
                className="w-full border-gray-300 dark:border-schmersal-gray-dark rounded-md shadow-sm 
                         focus:ring-blue-500 dark:focus:ring-schmersal-blue focus:border-blue-500 dark:focus:border-schmersal-blue 
                         bg-white dark:bg-schmersal-gray-darkest text-gray-900 dark:text-white
                         sm:text-sm"
              />
            ) : (
              <p className="text-sm text-gray-900 dark:text-white">
                {dados.operadorEntrevistado || '-'}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-white mb-2">
              {t('apreciacaoEdicao.informacoes.entrevista.cargo')}
            </label>
            {modoEdicao ? (
              <input
                type="text"
                value={dados.cargoOperador || ''}
                onChange={(e) => onChange?.('cargoOperador', e.target.value)}
                className="w-full border-gray-300 dark:border-schmersal-gray-dark rounded-md shadow-sm 
                         focus:ring-blue-500 dark:focus:ring-schmersal-blue focus:border-blue-500 dark:focus:border-schmersal-blue 
                         bg-white dark:bg-schmersal-gray-darkest text-gray-900 dark:text-white
                         sm:text-sm"
              />
            ) : (
              <p className="text-sm text-gray-900 dark:text-white">
                {dados.cargoOperador || '-'}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-white mb-2">
              {t('apreciacaoEdicao.informacoes.entrevista.experiencia')}
            </label>
            {modoEdicao ? (
              <input
                type="number"
                value={dados.experiencia || ''}
                onChange={(e) =>
                  onChange?.('experiencia', parseInt(e.target.value) || 0)
                }
                className="w-full border-gray-300 dark:border-schmersal-gray-dark rounded-md shadow-sm 
                         focus:ring-blue-500 dark:focus:ring-schmersal-blue focus:border-blue-500 dark:focus:border-schmersal-blue 
                         bg-white dark:bg-schmersal-gray-darkest text-gray-900 dark:text-white
                         sm:text-sm"
                min="0"
              />
            ) : (
              <p className="text-sm text-gray-900 dark:text-white">
                {t('apreciacaoEdicao.informacoes.entrevista.experienciaValor', { valor: dados.experiencia || '-' })}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-white mb-2">
              {t('apreciacaoEdicao.informacoes.entrevista.turnos')}
            </label>
            {modoEdicao ? (
              <input
                type="text"
                value={dados.turnosTrabalho || ''}
                onChange={(e) => onChange?.('turnosTrabalho', e.target.value)}
                className="w-full border-gray-300 dark:border-schmersal-gray-dark rounded-md shadow-sm 
                         focus:ring-blue-500 dark:focus:ring-schmersal-blue focus:border-blue-500 dark:focus:border-schmersal-blue 
                         bg-white dark:bg-schmersal-gray-darkest text-gray-900 dark:text-white
                         sm:text-sm"
                placeholder={t('apreciacaoEdicao.informacoes.entrevista.placeholderTurnos')}
              />
            ) : (
              <p className="text-sm text-gray-900 dark:text-white">
                {dados.turnosTrabalho || '-'}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-white mb-2">
              {t('apreciacaoEdicao.informacoes.entrevista.frequenciaUso')}
            </label>
            {modoEdicao ? (
              <input
                type="text"
                value={dados.frequenciaUso || ''}
                onChange={(e) => onChange?.('frequenciaUso', e.target.value)}
                className="w-full border-gray-300 dark:border-schmersal-gray-dark rounded-md shadow-sm 
                         focus:ring-blue-500 dark:focus:ring-schmersal-blue focus:border-blue-500 dark:focus:border-schmersal-blue 
                         bg-white dark:bg-schmersal-gray-darkest text-gray-900 dark:text-white
                         sm:text-sm"
                placeholder={t('apreciacaoEdicao.informacoes.entrevista.placeholderFrequenciaUso')}
              />
            ) : (
              <p className="text-sm text-gray-900 dark:text-white">
                {dados.frequenciaUso || '-'}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-white mb-2">
              {t('apreciacaoEdicao.informacoes.entrevista.frequenciaOperacao')}
            </label>
            {modoEdicao ? (
              <input
                type="text"
                value={dados.frequenciaAcessoOperacao || ''}
                onChange={(e) => onChange?.('frequenciaAcessoOperacao', e.target.value)}
                className="w-full border-gray-300 dark:border-schmersal-gray-dark rounded-md shadow-sm 
                         focus:ring-blue-500 dark:focus:ring-schmersal-blue focus:border-blue-500 dark:focus:border-schmersal-blue 
                         bg-white dark:bg-schmersal-gray-darkest text-gray-900 dark:text-white
                         sm:text-sm"
                placeholder={t('apreciacaoEdicao.informacoes.entrevista.placeholderFrequenciaOperacao')}
              />
            ) : (
              <p className="text-sm text-gray-900 dark:text-white">
                {dados.frequenciaAcessoOperacao || '-'}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-white mb-2">
              {t('apreciacaoEdicao.informacoes.entrevista.frequenciaManutencao')}
            </label>
            {modoEdicao ? (
              <input
                type="text"
                value={dados.frequenciaAcessoManutencao || ''}
                onChange={(e) => onChange?.('frequenciaAcessoManutencao', e.target.value)}
                className="w-full border-gray-300 dark:border-schmersal-gray-dark rounded-md shadow-sm 
                         focus:ring-blue-500 dark:focus:ring-schmersal-blue focus:border-blue-500 dark:focus:border-schmersal-blue 
                         bg-white dark:bg-schmersal-gray-darkest text-gray-900 dark:text-white
                         sm:text-sm"
                placeholder={t('apreciacaoEdicao.informacoes.entrevista.placeholderFrequenciaManutencao')}
              />
            ) : (
              <p className="text-sm text-gray-900 dark:text-white">
                {dados.frequenciaAcessoManutencao || '-'}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-white mb-2">
              {t('apreciacaoEdicao.informacoes.entrevista.dataEntrevista')}
            </label>
            {modoEdicao ? (
              <input
                type="date"
                value={dados.dataEntrevista || ''}
                onChange={(e) => onChange?.('dataEntrevista', e.target.value)}
                className="w-full border-gray-300 dark:border-schmersal-gray-dark rounded-md shadow-sm 
                         focus:ring-blue-500 dark:focus:ring-schmersal-blue focus:border-blue-500 dark:focus:border-schmersal-blue 
                         bg-white dark:bg-schmersal-gray-darkest text-gray-900 dark:text-white
                         sm:text-sm"
              />
            ) : (
              <p className="text-sm text-gray-900 dark:text-white">
                {dados.dataEntrevista ? formatarData(dados.dataEntrevista, { timeZone: 'UTC' }) : '-'}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-white mb-2">
              {t('apreciacaoEdicao.informacoes.entrevista.modoOperacao')}
            </label>
            {modoEdicao ? (
              <input
                type="text"
                value={dados.modoOperacao || ''}
                onChange={(e) => onChange?.('modoOperacao', e.target.value)}
                className="w-full border-gray-300 dark:border-schmersal-gray-dark rounded-md shadow-sm 
                         focus:ring-blue-500 dark:focus:ring-schmersal-blue focus:border-blue-500 dark:focus:border-schmersal-blue 
                         bg-white dark:bg-schmersal-gray-darkest text-gray-900 dark:text-white
                         sm:text-sm"
              />
            ) : (
              <p className="text-sm text-gray-900 dark:text-white">
                {dados.modoOperacao || '-'}
              </p>
            )}
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-white mb-2">
              {t('apreciacaoEdicao.informacoes.entrevista.historicoAcidentes')}
            </label>
            {modoEdicao ? (
              <textarea
                value={dados.historicoAcidentes || ''}
                onChange={(e) =>
                  onChange?.('historicoAcidentes', e.target.value)
                }
                rows={3}
                className="w-full border-gray-300 dark:border-schmersal-gray-dark rounded-md shadow-sm 
                         focus:ring-blue-500 dark:focus:ring-schmersal-blue focus:border-blue-500 dark:focus:border-schmersal-blue 
                         bg-white dark:bg-schmersal-gray-darkest text-gray-900 dark:text-white
                         sm:text-sm"
              />
            ) : (
              <p className="text-sm text-gray-900 dark:text-white">
                {dados.historicoAcidentes || '-'}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Informações Técnicas da Máquina */}
      <div className="border-b dark:border-schmersal-gray-dark pb-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
          <Settings className="h-5 w-5 mr-2" />
          {t('apreciacaoEdicao.informacoes.tecnicas.titulo')}
        </h3>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-white mb-2">
              {t('apreciacaoEdicao.informacoes.tecnicas.comprimentoHorizontal')}
            </label>
            {modoEdicao ? (
              <input
                type="text"
                value={dados.comprimentoHorizontal || ''}
                onChange={(e) => onChange?.('comprimentoHorizontal', e.target.value)}
                className="w-full border-gray-300 dark:border-schmersal-gray-dark rounded-md shadow-sm 
                         focus:ring-blue-500 dark:focus:ring-schmersal-blue focus:border-blue-500 dark:focus:border-schmersal-blue 
                         bg-white dark:bg-schmersal-gray-darkest text-gray-900 dark:text-white
                         sm:text-sm"
                placeholder={t('apreciacaoEdicao.informacoes.tecnicas.placeholderComprimentoHorizontal')}
              />
            ) : (
              <p className="text-sm text-gray-900 dark:text-white">
                {dados.comprimentoHorizontal || '-'}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-white mb-2">
              {t('apreciacaoEdicao.informacoes.tecnicas.elevacao')}
            </label>
            {modoEdicao ? (
              <input
                type="text"
                value={dados.elevacao || ''}
                onChange={(e) => onChange?.('elevacao', e.target.value)}
                className="w-full border-gray-300 dark:border-schmersal-gray-dark rounded-md shadow-sm 
                         focus:ring-blue-500 dark:focus:ring-schmersal-blue focus:border-blue-500 dark:focus:border-schmersal-blue 
                         bg-white dark:bg-schmersal-gray-darkest text-gray-900 dark:text-white
                         sm:text-sm"
                placeholder={t('apreciacaoEdicao.informacoes.tecnicas.placeholderNaoInformado')}
              />
            ) : (
              <p className="text-sm text-gray-900 dark:text-white">
                {dados.elevacao || '-'}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-white mb-2">
              {t('apreciacaoEdicao.informacoes.tecnicas.capacidadeCorreia')}
            </label>
            {modoEdicao ? (
              <input
                type="text"
                value={dados.capacidadeCorreia || ''}
                onChange={(e) => onChange?.('capacidadeCorreia', e.target.value)}
                className="w-full border-gray-300 dark:border-schmersal-gray-dark rounded-md shadow-sm 
                         focus:ring-blue-500 dark:focus:ring-schmersal-blue focus:border-blue-500 dark:focus:border-schmersal-blue 
                         bg-white dark:bg-schmersal-gray-darkest text-gray-900 dark:text-white
                         sm:text-sm"
                placeholder={t('apreciacaoEdicao.informacoes.tecnicas.placeholderCapacidadeCorreia')}
              />
            ) : (
              <p className="text-sm text-gray-900 dark:text-white">
                {dados.capacidadeCorreia || '-'}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-white mb-2">
              {t('apreciacaoEdicao.informacoes.tecnicas.larguraCorreia')}
            </label>
            {modoEdicao ? (
              <input
                type="text"
                value={dados.larguraCorreia || ''}
                onChange={(e) => onChange?.('larguraCorreia', e.target.value)}
                className="w-full border-gray-300 dark:border-schmersal-gray-dark rounded-md shadow-sm 
                         focus:ring-blue-500 dark:focus:ring-schmersal-blue focus:border-blue-500 dark:focus:border-schmersal-blue 
                         bg-white dark:bg-schmersal-gray-darkest text-gray-900 dark:text-white
                         sm:text-sm"
                placeholder={t('apreciacaoEdicao.informacoes.tecnicas.placeholderLarguraCorreia')}
              />
            ) : (
              <p className="text-sm text-gray-900 dark:text-white">
                {dados.larguraCorreia || '-'}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-white mb-2">
              {t('apreciacaoEdicao.informacoes.tecnicas.velocidadeCorreia')}
            </label>
            {modoEdicao ? (
              <input
                type="text"
                value={dados.velocidadeCorreia || ''}
                onChange={(e) => onChange?.('velocidadeCorreia', e.target.value)}
                className="w-full border-gray-300 dark:border-schmersal-gray-dark rounded-md shadow-sm 
                         focus:ring-blue-500 dark:focus:ring-schmersal-blue focus:border-blue-500 dark:focus:border-schmersal-blue 
                         bg-white dark:bg-schmersal-gray-darkest text-gray-900 dark:text-white
                         sm:text-sm"
                placeholder={t('apreciacaoEdicao.informacoes.tecnicas.placeholderVelocidadeCorreia')}
              />
            ) : (
              <p className="text-sm text-gray-900 dark:text-white">
                {dados.velocidadeCorreia || '-'}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-white mb-2">
              {t('apreciacaoEdicao.informacoes.tecnicas.material')}
            </label>
            {modoEdicao ? (
              <input
                type="text"
                value={dados.material || ''}
                onChange={(e) => onChange?.('material', e.target.value)}
                className="w-full border-gray-300 dark:border-schmersal-gray-dark rounded-md shadow-sm 
                         focus:ring-blue-500 dark:focus:ring-schmersal-blue focus:border-blue-500 dark:focus:border-schmersal-blue 
                         bg-white dark:bg-schmersal-gray-darkest text-gray-900 dark:text-white
                         sm:text-sm"
                placeholder={t('apreciacaoEdicao.informacoes.tecnicas.placeholderNaoInformado')}
              />
            ) : (
              <p className="text-sm text-gray-900 dark:text-white">
                {dados.material || '-'}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-white mb-2">
              {t('apreciacaoEdicao.informacoes.tecnicas.contraRecuo')}
            </label>
            {modoEdicao ? (
              <input
                type="text"
                value={dados.contraRecuo || ''}
                onChange={(e) => onChange?.('contraRecuo', e.target.value)}
                className="w-full border-gray-300 dark:border-schmersal-gray-dark rounded-md shadow-sm 
                         focus:ring-blue-500 dark:focus:ring-schmersal-blue focus:border-blue-500 dark:focus:border-schmersal-blue 
                         bg-white dark:bg-schmersal-gray-darkest text-gray-900 dark:text-white
                         sm:text-sm"
                placeholder={t('apreciacaoEdicao.informacoes.tecnicas.placeholderNaoInformado')}
              />
            ) : (
              <p className="text-sm text-gray-900 dark:text-white">
                {dados.contraRecuo || '-'}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-white mb-2">
              {t('apreciacaoEdicao.informacoes.tecnicas.freio')}
            </label>
            {modoEdicao ? (
              <input
                type="text"
                value={dados.freio || ''}
                onChange={(e) => onChange?.('freio', e.target.value)}
                className="w-full border-gray-300 dark:border-schmersal-gray-dark rounded-md shadow-sm 
                         focus:ring-blue-500 dark:focus:ring-schmersal-blue focus:border-blue-500 dark:focus:border-schmersal-blue 
                         bg-white dark:bg-schmersal-gray-darkest text-gray-900 dark:text-white
                         sm:text-sm"
                placeholder={t('apreciacaoEdicao.informacoes.tecnicas.placeholderNaoInformado')}
              />
            ) : (
              <p className="text-sm text-gray-900 dark:text-white">
                {dados.freio || '-'}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-white mb-2">
              {t('apreciacaoEdicao.informacoes.tecnicas.potencia')}
            </label>
            {modoEdicao ? (
              <input
                type="text"
                value={dados.potencia || ''}
                onChange={(e) => onChange?.('potencia', e.target.value)}
                className="w-full border-gray-300 dark:border-schmersal-gray-dark rounded-md shadow-sm 
                         focus:ring-blue-500 dark:focus:ring-schmersal-blue focus:border-blue-500 dark:focus:border-schmersal-blue 
                         bg-white dark:bg-schmersal-gray-darkest text-gray-900 dark:text-white
                         sm:text-sm"
                placeholder={t('apreciacaoEdicao.informacoes.tecnicas.placeholderPotencia')}
              />
            ) : (
              <p className="text-sm text-gray-900 dark:text-white">
                {dados.potencia || '-'}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-white mb-2">
              {t('apreciacaoEdicao.informacoes.tecnicas.tensaoAcionamento')}
            </label>
            {modoEdicao ? (
              <input
                type="text"
                value={dados.tensaoAcionamento || ''}
                onChange={(e) => onChange?.('tensaoAcionamento', e.target.value)}
                className="w-full border-gray-300 dark:border-schmersal-gray-dark rounded-md shadow-sm 
                         focus:ring-blue-500 dark:focus:ring-schmersal-blue focus:border-blue-500 dark:focus:border-schmersal-blue 
                         bg-white dark:bg-schmersal-gray-darkest text-gray-900 dark:text-white
                         sm:text-sm"
                placeholder={t('apreciacaoEdicao.informacoes.tecnicas.placeholderTensao')}
              />
            ) : (
              <p className="text-sm text-gray-900 dark:text-white">
                {dados.tensaoAcionamento || '-'}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-white mb-2">
              {t('apreciacaoEdicao.informacoes.tecnicas.tensaoComando')}
            </label>
            {modoEdicao ? (
              <input
                type="text"
                value={dados.tensaoComando || ''}
                onChange={(e) => onChange?.('tensaoComando', e.target.value)}
                className="w-full border-gray-300 dark:border-schmersal-gray-dark rounded-md shadow-sm 
                         focus:ring-blue-500 dark:focus:ring-schmersal-blue focus:border-blue-500 dark:focus:border-schmersal-blue 
                         bg-white dark:bg-schmersal-gray-darkest text-gray-900 dark:text-white
                         sm:text-sm"
                placeholder={t('apreciacaoEdicao.informacoes.tecnicas.placeholderTensao')}
              />
            ) : (
              <p className="text-sm text-gray-900 dark:text-white">
                {dados.tensaoComando || '-'}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Limites da Máquina */}
      <div className="border-b dark:border-schmersal-gray-dark pb-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
          <AlertTriangle className="h-5 w-5 mr-2" />
          {t('apreciacaoEdicao.informacoes.limites.titulo')}
        </h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-white mb-2">
              {t('apreciacaoEdicao.informacoes.limites.uso')}
            </label>
            {modoEdicao ? (
              <textarea
                value={dados.limitesUso || ''}
                onChange={(e) => onChange?.('limitesUso', e.target.value)}
                rows={3}
                className="w-full border-gray-300 dark:border-schmersal-gray-dark rounded-md shadow-sm 
                         focus:ring-blue-500 dark:focus:ring-schmersal-blue focus:border-blue-500 dark:focus:border-schmersal-blue 
                         bg-white dark:bg-schmersal-gray-darkest text-gray-900 dark:text-white
                         sm:text-sm"
              />
            ) : (
              <p className="text-sm text-gray-900 dark:text-white">{dados.limitesUso || '-'}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-white mb-2">
              {t('apreciacaoEdicao.informacoes.limites.espaco')}
            </label>
            {modoEdicao ? (
              <textarea
                value={dados.limitesEspaco || ''}
                onChange={(e) => onChange?.('limitesEspaco', e.target.value)}
                rows={3}
                className="w-full border-gray-300 dark:border-schmersal-gray-dark rounded-md shadow-sm 
                         focus:ring-blue-500 dark:focus:ring-schmersal-blue focus:border-blue-500 dark:focus:border-schmersal-blue 
                         bg-white dark:bg-schmersal-gray-darkest text-gray-900 dark:text-white
                         sm:text-sm"
              />
            ) : (
              <p className="text-sm text-gray-900 dark:text-white">
                {dados.limitesEspaco || '-'}
              </p>
            )}
          </div>

          {/* Espaços Disponíveis */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-white mb-3">
              {t('apreciacaoEdicao.informacoes.limites.espacosTitulo')}
            </label>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-700 dark:text-white">
                  {t('apreciacaoEdicao.informacoes.limites.espacoPecasFerramentas')}
                </span>
                {modoEdicao ? (
                  <div className="flex gap-4">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="espacoPecasFerramentas"
                        checked={dados.espacoPecasFerramentas === true}
                        onChange={() => onChange?.('espacoPecasFerramentas', true)}
                        className="mr-2"
                      />
                      <span className="text-sm text-gray-700 dark:text-white">{t('comum.rotulo.sim')}</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="espacoPecasFerramentas"
                        checked={dados.espacoPecasFerramentas === false}
                        onChange={() => onChange?.('espacoPecasFerramentas', false)}
                        className="mr-2"
                      />
                      <span className="text-sm text-gray-700 dark:text-white">{t('comum.rotulo.nao')}</span>
                    </label>
                  </div>
                ) : (
                  <span className="text-sm text-gray-900 dark:text-white">
                    {dados.espacoPecasFerramentas === true ? t('comum.rotulo.sim') : dados.espacoPecasFerramentas === false ? t('comum.rotulo.nao') : '-'}
                  </span>
                )}
              </div>

              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-700 dark:text-white">
                  {t('apreciacaoEdicao.informacoes.limites.espacoOperacaoNormal')}
                </span>
                {modoEdicao ? (
                  <div className="flex gap-4">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="espacoOperacaoNormal"
                        checked={dados.espacoOperacaoNormal === true}
                        onChange={() => onChange?.('espacoOperacaoNormal', true)}
                        className="mr-2"
                      />
                      <span className="text-sm text-gray-700 dark:text-white">{t('comum.rotulo.sim')}</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="espacoOperacaoNormal"
                        checked={dados.espacoOperacaoNormal === false}
                        onChange={() => onChange?.('espacoOperacaoNormal', false)}
                        className="mr-2"
                      />
                      <span className="text-sm text-gray-700 dark:text-white">{t('comum.rotulo.nao')}</span>
                    </label>
                  </div>
                ) : (
                  <span className="text-sm text-gray-900 dark:text-white">
                    {dados.espacoOperacaoNormal === true ? t('comum.rotulo.sim') : dados.espacoOperacaoNormal === false ? t('comum.rotulo.nao') : '-'}
                  </span>
                )}
              </div>

              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-700 dark:text-white">
                  {t('apreciacaoEdicao.informacoes.limites.espacoManutencao')}
                </span>
                {modoEdicao ? (
                  <div className="flex gap-4">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="espacoManutencao"
                        checked={dados.espacoManutencao === true}
                        onChange={() => onChange?.('espacoManutencao', true)}
                        className="mr-2"
                      />
                      <span className="text-sm text-gray-700 dark:text-white">{t('comum.rotulo.sim')}</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="espacoManutencao"
                        checked={dados.espacoManutencao === false}
                        onChange={() => onChange?.('espacoManutencao', false)}
                        className="mr-2"
                      />
                      <span className="text-sm text-gray-700 dark:text-white">{t('comum.rotulo.nao')}</span>
                    </label>
                  </div>
                ) : (
                  <span className="text-sm text-gray-900 dark:text-white">
                    {dados.espacoManutencao === true ? t('comum.rotulo.sim') : dados.espacoManutencao === false ? t('comum.rotulo.nao') : '-'}
                  </span>
                )}
              </div>

              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-700 dark:text-white">
                  {t('apreciacaoEdicao.informacoes.limites.espacoAjuste')}
                </span>
                {modoEdicao ? (
                  <div className="flex gap-4">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="espacoAjuste"
                        checked={dados.espacoAjuste === true}
                        onChange={() => onChange?.('espacoAjuste', true)}
                        className="mr-2"
                      />
                      <span className="text-sm text-gray-700 dark:text-white">{t('comum.rotulo.sim')}</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="espacoAjuste"
                        checked={dados.espacoAjuste === false}
                        onChange={() => onChange?.('espacoAjuste', false)}
                        className="mr-2"
                      />
                      <span className="text-sm text-gray-700 dark:text-white">{t('comum.rotulo.nao')}</span>
                    </label>
                  </div>
                ) : (
                  <span className="text-sm text-gray-900 dark:text-white">
                    {dados.espacoAjuste === true ? t('comum.rotulo.sim') : dados.espacoAjuste === false ? t('comum.rotulo.nao') : '-'}
                  </span>
                )}
              </div>

              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-700 dark:text-white">
                  {t('apreciacaoEdicao.informacoes.limites.espacoLimpeza')}
                </span>
                {modoEdicao ? (
                  <div className="flex gap-4">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="espacoLimpeza"
                        checked={dados.espacoLimpeza === true}
                        onChange={() => onChange?.('espacoLimpeza', true)}
                        className="mr-2"
                      />
                      <span className="text-sm text-gray-700 dark:text-white">{t('comum.rotulo.sim')}</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="espacoLimpeza"
                        checked={dados.espacoLimpeza === false}
                        onChange={() => onChange?.('espacoLimpeza', false)}
                        className="mr-2"
                      />
                      <span className="text-sm text-gray-700 dark:text-white">{t('comum.rotulo.nao')}</span>
                    </label>
                  </div>
                ) : (
                  <span className="text-sm text-gray-900 dark:text-white">
                    {dados.espacoLimpeza === true ? t('comum.rotulo.sim') : dados.espacoLimpeza === false ? t('comum.rotulo.nao') : '-'}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Imagens da Máquina */}
          <div className="border-b dark:border-schmersal-gray-dark pb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
              <ImageIcon className="h-5 w-5 mr-2" />
              {t('apreciacaoEdicao.informacoes.imagens.titulo')}
            </h3>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              {/* Foto Panorâmica */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-white mb-2">
                  {t('apreciacaoEdicao.informacoes.imagens.panoramica')}
                </label>
                {modoEdicao ? (
                  <div>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          try {
                            onChange?.('fotoPanoramicaUrl', await comprimirImagemParaDataUrl(file));
                          } catch (error) {
                            console.error('Erro ao processar a foto panorâmica:', error);
                            alert(t('apreciacaoEdicao.informacoes.imagens.erroProcessar'));
                          }
                        }
                      }}
                      className="hidden"
                      id="upload-panoramica"
                    />
                    <label
                      htmlFor="upload-panoramica"
                      className="flex items-center justify-center w-full min-h-32 border-2 border-dashed border-gray-300 dark:border-schmersal-gray-dark rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-schmersal-gray-dark transition-colors"
                    >
                      {dados.fotoPanoramicaUrl ? (
                        <div className="relative w-full min-h-32 flex items-center justify-center">
                          <ImagemComFallback
                            src={dados.fotoPanoramicaUrl}
                            alt={t('apreciacaoEdicao.informacoes.imagens.altPanoramica')}
                            className="w-full max-h-64 object-contain rounded-lg"
                          />
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              onChange?.('fotoPanoramicaUrl', '');
                            }}
                            className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 z-10"
                            title={t('comum.acao.remover')}
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center py-4">
                          <Upload className="h-8 w-8 text-gray-400 mb-2" />
                          <span className="text-sm text-gray-500 dark:text-gray-400">
                            {t('comum.arquivo.nenhumEscolhido')}
                          </span>
                          <span className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                            {t('comum.arquivo.cliqueUpload')}
                          </span>
                        </div>
                      )}
                    </label>
                  </div>
                ) : (
                  <div className="flex justify-center items-center min-h-32">
                    {dados.fotoPanoramicaUrl ? (
                      <ImagemComFallback
                        src={dados.fotoPanoramicaUrl}
                        alt={t('apreciacaoEdicao.informacoes.imagens.altPanoramica')}
                        className="max-w-full max-h-64 object-contain rounded-lg border border-gray-300 dark:border-schmersal-gray-dark"
                      />
                    ) : (
                      <p className="text-sm text-gray-500 dark:text-gray-400">{t('comum.arquivo.nenhumEscolhido')}</p>
                    )}
                  </div>
                )}
              </div>

              {/* Foto Identificação */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-white mb-2">
                  {t('apreciacaoEdicao.informacoes.imagens.identificacao')}
                </label>
                {modoEdicao ? (
                  <div>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          try {
                            onChange?.('fotoIdentificacaoUrl', await comprimirImagemParaDataUrl(file));
                          } catch (error) {
                            console.error('Erro ao processar a foto de identificação:', error);
                            alert(t('apreciacaoEdicao.informacoes.imagens.erroProcessar'));
                          }
                        }
                      }}
                      className="hidden"
                      id="upload-identificacao"
                    />
                    <label
                      htmlFor="upload-identificacao"
                      className="flex items-center justify-center w-full min-h-32 border-2 border-dashed border-gray-300 dark:border-schmersal-gray-dark rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-schmersal-gray-dark transition-colors"
                    >
                      {dados.fotoIdentificacaoUrl ? (
                        <div className="relative w-full min-h-32 flex items-center justify-center">
                          <ImagemComFallback
                            src={dados.fotoIdentificacaoUrl}
                            alt={t('apreciacaoEdicao.informacoes.imagens.altIdentificacao')}
                            className="w-full max-h-64 object-contain rounded-lg"
                          />
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              onChange?.('fotoIdentificacaoUrl', '');
                            }}
                            className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 z-10"
                            title={t('comum.acao.remover')}
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center py-4">
                          <Upload className="h-8 w-8 text-gray-400 mb-2" />
                          <span className="text-sm text-gray-500 dark:text-gray-400">
                            {t('comum.arquivo.nenhumEscolhido')}
                          </span>
                          <span className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                            {t('comum.arquivo.cliqueUpload')}
                          </span>
                        </div>
                      )}
                    </label>
                  </div>
                ) : (
                  <div className="flex justify-center items-center min-h-32">
                    {dados.fotoIdentificacaoUrl ? (
                      <ImagemComFallback
                        src={dados.fotoIdentificacaoUrl}
                        alt={t('apreciacaoEdicao.informacoes.imagens.altIdentificacao')}
                        className="max-w-full max-h-64 object-contain rounded-lg border border-gray-300 dark:border-schmersal-gray-dark"
                      />
                    ) : (
                      <p className="text-sm text-gray-500 dark:text-gray-400">{t('comum.arquivo.nenhumEscolhido')}</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-white mb-2">
              {t('apreciacaoEdicao.informacoes.limites.adicionais')}
            </label>
            {modoEdicao ? (
              <textarea
                value={dados.limitesAdicionais || ''}
                onChange={(e) =>
                  onChange?.('limitesAdicionais', e.target.value)
                }
                rows={3}
                className="w-full border-gray-300 dark:border-schmersal-gray-dark rounded-md shadow-sm 
                         focus:ring-blue-500 dark:focus:ring-schmersal-blue focus:border-blue-500 dark:focus:border-schmersal-blue 
                         bg-white dark:bg-schmersal-gray-darkest text-gray-900 dark:text-white
                         sm:text-sm"
              />
            ) : (
              <p className="text-sm text-gray-900 dark:text-white">
                {dados.limitesAdicionais || '-'}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-white mb-2">
              {t('apreciacaoEdicao.informacoes.limites.ambientais')}
            </label>
            {modoEdicao ? (
              <textarea
                value={dados.limitesAmbientais || ''}
                onChange={(e) =>
                  onChange?.('limitesAmbientais', e.target.value)
                }
                rows={3}
                className="w-full border-gray-300 dark:border-schmersal-gray-dark rounded-md shadow-sm 
                         focus:ring-blue-500 dark:focus:ring-schmersal-blue focus:border-blue-500 dark:focus:border-schmersal-blue 
                         bg-white dark:bg-schmersal-gray-darkest text-gray-900 dark:text-white
                         sm:text-sm"
              />
            ) : (
              <p className="text-sm text-gray-900 dark:text-white">
                {dados.limitesAmbientais || '-'}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Mau Uso Razoavelmente Previsível */}
      <div className="border-b dark:border-schmersal-gray-dark pb-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          {t('apreciacaoEdicao.informacoes.mauUso.titulo')}
        </h3>
        {modoEdicao ? (
          <div className="space-y-2">
            {(dados.mauUsoRazoavelmentePrevisivel || []).map((item, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-2 bg-gray-50 dark:bg-schmersal-gray-dark rounded"
              >
                <span className="text-sm text-gray-900 dark:text-white">
                  {index + 1}. {item}
                </span>
                <button
                  onClick={() => removerMauUso(index)}
                  className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ))}
            {(dados.mauUsoRazoavelmentePrevisivel || []).length < 5 && (
              <div className="flex gap-2">
                <input
                  type="text"
                  value={novoMauUso}
                  onChange={(e) => setNovoMauUso(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && adicionarMauUso()}
                  className="flex-1 border-gray-300 dark:border-schmersal-gray-dark rounded-md shadow-sm 
                           focus:ring-blue-500 dark:focus:ring-schmersal-blue focus:border-blue-500 dark:focus:border-schmersal-blue 
                           bg-white dark:bg-schmersal-gray-darkest text-gray-900 dark:text-white
                           sm:text-sm"
                  placeholder={t('apreciacaoEdicao.informacoes.mauUso.placeholder')}
                />
                <button
                  onClick={adicionarMauUso}
                  className="px-3 py-2 bg-blue-600 dark:bg-schmersal-blue text-white rounded-md hover:bg-blue-700 dark:hover:bg-schmersal-blue-light"
                >
                  <Plus className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>
        ) : (
          <ul className="list-decimal list-inside space-y-1">
            {(dados.mauUsoRazoavelmentePrevisivel || []).map((item, index) => (
              <li key={index} className="text-sm text-gray-900 dark:text-white">
                {typeof item === 'object' ? JSON.stringify(item) : String(item ?? '')}
              </li>
            ))}
            {(!dados.mauUsoRazoavelmentePrevisivel ||
              dados.mauUsoRazoavelmentePrevisivel.length === 0) && (
              <li className="text-sm text-gray-500 dark:text-schmersal-gray-medium">{t('apreciacaoEdicao.informacoes.mauUso.vazio')}</li>
            )}
          </ul>
        )}
      </div>

      {/* Normas Técnicas de Referência */}
      <div className="border-b dark:border-schmersal-gray-dark pb-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
          <FileText className="h-5 w-5 mr-2" />
          {t('apreciacaoEdicao.informacoes.normas.titulo')}
        </h3>
        {modoEdicao ? (
          <div className="space-y-2">
            {(dados.normasTecnicasReferencia || []).map((norma, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-2 bg-gray-50 dark:bg-schmersal-gray-dark rounded"
              >
                <span className="text-sm text-gray-900 dark:text-white">{norma}</span>
                <button
                  onClick={() => removerNorma(index)}
                  className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ))}
            <div className="flex gap-2">
              <input
                type="text"
                value={novaNorma}
                onChange={(e) => setNovaNorma(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && adicionarNorma()}
                className="flex-1 border-gray-300 dark:border-schmersal-gray-dark rounded-md shadow-sm 
                         focus:ring-blue-500 dark:focus:ring-schmersal-blue focus:border-blue-500 dark:focus:border-schmersal-blue 
                         bg-white dark:bg-schmersal-gray-darkest text-gray-900 dark:text-white
                         sm:text-sm"
                placeholder={t('apreciacaoEdicao.informacoes.normas.placeholder')}
              />
              <button
                onClick={adicionarNorma}
                className="px-3 py-2 bg-blue-600 dark:bg-schmersal-blue text-white rounded-md hover:bg-blue-700 dark:hover:bg-schmersal-blue-light"
              >
                <Plus className="h-4 w-4" />
              </button>
            </div>
          </div>
        ) : (
          <ul className="list-disc list-inside space-y-1">
            {(dados.normasTecnicasReferencia || []).map((norma, index) => (
              <li key={index} className="text-sm text-gray-900 dark:text-white">
                {typeof norma === 'object' ? JSON.stringify(norma) : String(norma ?? '')}
              </li>
            ))}
            {(!dados.normasTecnicasReferencia ||
              dados.normasTecnicasReferencia.length === 0) && (
              <li className="text-sm text-gray-500 dark:text-schmersal-gray-medium">{t('apreciacaoEdicao.informacoes.normas.vazio')}</li>
            )}
          </ul>
        )}
      </div>

      {/* Informações adicionais da máquina */}
      {!modoEdicao && extraFields && Object.keys(extraFields).length > 0 && (
        <div className="border-b dark:border-schmersal-gray-dark pb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            {t('apreciacaoEdicao.informacoes.extras.titulo')}
          </h3>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {Object.entries(extraFields).map(([key, value]) => (
              <div key={key}>
                <label className="block text-sm font-medium text-gray-700 dark:text-white mb-2">
                  {formatExtraFieldLabel(key)}
                </label>
                <p className="text-sm text-gray-900 dark:text-white whitespace-pre-wrap break-words">
                  {value}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Notas */}
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-white mb-2">
            {t('apreciacaoEdicao.informacoes.notas.nota1')}
          </label>
          {modoEdicao ? (
            <textarea
              value={dados.nota1 || ''}
              onChange={(e) => onChange?.('nota1', e.target.value)}
              rows={3}
              className="w-full border-gray-300 dark:border-schmersal-gray-dark rounded-md shadow-sm 
                       focus:ring-blue-500 dark:focus:ring-schmersal-blue focus:border-blue-500 dark:focus:border-schmersal-blue 
                       bg-white dark:bg-schmersal-gray-darkest text-gray-900 dark:text-white
                       sm:text-sm"
            />
          ) : (
            <p className="text-sm text-gray-900 dark:text-white">{dados.nota1 || '-'}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-white mb-2">
            {t('apreciacaoEdicao.informacoes.notas.nota2')}
          </label>
          {modoEdicao ? (
            <textarea
              value={dados.nota2 || ''}
              onChange={(e) => onChange?.('nota2', e.target.value)}
              rows={3}
              className="w-full border-gray-300 dark:border-schmersal-gray-dark rounded-md shadow-sm 
                       focus:ring-blue-500 dark:focus:ring-schmersal-blue focus:border-blue-500 dark:focus:border-schmersal-blue 
                       bg-white dark:bg-schmersal-gray-darkest text-gray-900 dark:text-white
                       sm:text-sm"
            />
          ) : (
            <p className="text-sm text-gray-900 dark:text-white">{dados.nota2 || '-'}</p>
          )}
        </div>
      </div>
    </div>
  );
}
